import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    IconArrowLeft,
    IconWallet,
    IconArrowUpRight,
    IconArrowDownRight
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { colors, fonts } from '../../ColorFont/color';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';

// ===== UNIVERSAL TOKENS =====
// Tokens used: carbon_gradient_earning, carbon_wallet_btn_bg, carbon_wallet_tab_container, etc. (Setup in themes.js)

// ===== TRANSLATIONS payload directly nested as requested =====
const pageTranslations = {
    en: {
        back: "Back",
        myWallet: "My Wallet",
        manageEarnings: "Manage your carbon credit earnings",
        availableBalance: "Available Balance",
        withdrawFunds: "Withdraw Funds",
        earnings: "Earnings",
        withdrawals: "Withdrawals",
        noEarningsYet: "No Earnings Yet",
        salesWillAppearHere: "Your carbon credit sales will appear here"
    },
    hi: {
        back: "वापस",
        myWallet: "मेरा वॉलेट",
        manageEarnings: "अपने कार्बन क्रेडिट कमाई का प्रबंधन करें",
        availableBalance: "उपलब्ध शेष राशि",
        withdrawFunds: "पैसे निकालें",
        earnings: "कमाई",
        withdrawals: "निकासी",
        noEarningsYet: "अभी तक कोई कमाई नहीं",
        salesWillAppearHere: "आपकी कार्बन क्रेडिट बिक्री यहां दिखाई देगी"
    },
    mr: {
        back: "मागे",
        myWallet: "माझे वॉलेट",
        manageEarnings: "तुमच्या कार्बन क्रेडिट कमाईचे व्यवस्थापन करा",
        availableBalance: "उपलब्ध रक्कम",
        withdrawFunds: "निधी काढा",
        earnings: "कमाई",
        withdrawals: "काढलेली रक्कम",
        noEarningsYet: "अद्याप कोणतीही कमाई नाही",
        salesWillAppearHere: "तुमची कार्बन क्रेडिट विक्री येथे दिसेल"
    }
};

const CarbonCreditsMyWallet = ({ onBack }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = pageTranslations[currentLang] || pageTranslations['en'];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('earnings'); // 'earnings' | 'withdrawals'

    const handleBack = () => {
        if (onBack) onBack();
        else navigate(-1);
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: `${colors.background_light || '#FCFDFC'} !important`,
            display: 'flex',
            justifyContent: 'center',
            pt: { xs: 2, md: 4 },
            pb: { xs: 4, md: 6 }
        }}>
            <Box sx={{
                width: '100%',
                maxWidth: '896px',
                px: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
            }}>

                {/* ===== NAVIGATION & TITLE ===== */}
                <Box>
                    <Button
                        onClick={handleBack}
                        variant="outlined"
                        startIcon={<IconArrowLeft size={18} stroke={2.5} />}
                        sx={{
                            borderRadius: '50px',
                            px: 3,
                            height: '48px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: '#E0E5EB',
                            mb: '24px',
                            minWidth: '120px',
                            '&:hover': {
                                borderColor: '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>

                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontWeight: 700,
                        fontSize: '24px',
                        lineHeight: '32px',
                        letterSpacing: '-0.6px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '4px'
                    }}>
                        {tLocal.myWallet}
                    </Typography>

                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontWeight: 400,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: colors.carbon_text_secondary || '#65758B'
                    }}>
                        {tLocal.manageEarnings}
                    </Typography>
                </Box>


                {/* ===== WALLET CARD ===== */}
                <Box sx={{
                    width: '100%',
                    background: colors.carbon_gradient_earning || 'linear-gradient(135deg, #1A4D2E 0%, #10451D 100%)',
                    borderRadius: '12px',
                    p: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        {/* Icon Box */}
                        <Box sx={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)', // #FFFFFF33 approximate
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <IconWallet size={24} color="#FFFFFF" stroke={1.5} />
                        </Box>

                        <Box>
                            <Typography sx={{
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontSize: '14px',
                                fontWeight: 400,
                                lineHeight: '20px',
                                color: colors.carbon_text_earning_bg || '#C8E6C9'
                            }}>
                                {tLocal.availableBalance}
                            </Typography>
                            <Typography sx={{
                                fontFamily: fonts.manrope || fonts.poppins,
                                fontSize: '36px',
                                fontWeight: 700,
                                lineHeight: '40px',
                                color: '#FFFFFF'
                            }}>
                                ₹0
                            </Typography>
                        </Box>
                    </Box>

                    <Button
                        fullWidth
                        startIcon={<IconArrowUpRight size={18} stroke={2} />}
                        sx={{
                            backgroundColor: 'rgba(252, 254, 252, 0.5)', // #FCFEFC with opacity 0.5 according to exact design spec text
                            height: '36px',
                            borderRadius: '14px',
                            color: colors.carbon_wallet_btn_text || '#10451D',
                            textTransform: 'none',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 500,
                            fontSize: '14px',
                            boxShadow: '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(252, 254, 252, 0.65)'
                            }
                        }}
                    >
                        {tLocal.withdrawFunds}
                    </Button>
                </Box>


                {/* ===== TABS ===== */}
                <Box sx={{
                    width: '100%',
                    backgroundColor: colors.carbon_wallet_tab_container || '#F3F5F7',
                    borderRadius: '16px',
                    p: '4px',
                    display: 'flex'
                }}>
                    <Box
                        onClick={() => setActiveTab('earnings')}
                        sx={{
                            flex: 1,
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            backgroundColor: activeTab === 'earnings' ? (colors.carbon_wallet_tab_bg || '#FFFFFF') : 'transparent',
                            boxShadow: activeTab === 'earnings' ? '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s',
                        }}
                    >
                        <IconArrowDownRight size={16} color={activeTab === 'earnings' ? '#0F1729' : '#65758B'} stroke={1.5} />
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: activeTab === 'earnings' ? (colors.carbon_text_main || '#0F1729') : (colors.carbon_text_secondary || '#65758B')
                        }}>
                            {tLocal.earnings}
                        </Typography>
                    </Box>

                    <Box
                        onClick={() => setActiveTab('withdrawals')}
                        sx={{
                            flex: 1,
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            borderRadius: '14px',
                            cursor: 'pointer',
                            backgroundColor: activeTab === 'withdrawals' ? (colors.carbon_wallet_tab_bg || '#FFFFFF') : 'transparent',
                            boxShadow: activeTab === 'withdrawals' ? '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s',
                        }}
                    >
                        <IconArrowUpRight size={16} color={activeTab === 'withdrawals' ? '#0F1729' : '#65758B'} stroke={1.5} />
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            color: activeTab === 'withdrawals' ? (colors.carbon_text_main || '#0F1729') : (colors.carbon_text_secondary || '#65758B')
                        }}>
                            {tLocal.withdrawals}
                        </Typography>
                    </Box>
                </Box>


                {/* ===== EMPTY STATE PANEL ===== */}
                <Box sx={{
                    width: '100%',
                    height: '184px',
                    backgroundColor: '#FFFFFF',
                    border: `2px dashed ${colors.carbon_border_solid || '#E0E5EB'}`,
                    borderRadius: '12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 1px 2px -1px rgba(0,0,0,0.1), 0px 1px 3px 0px rgba(0,0,0,0.1)'
                }}>
                    <IconArrowDownRight size={40} color={colors.carbon_text_secondary || '#65758B'} stroke={1.5} style={{ marginBottom: '16px' }} />
                    <Typography sx={{
                        fontFamily: fonts.manrope || fonts.poppins,
                        fontSize: '16px',
                        fontWeight: 600,
                        lineHeight: '24px',
                        letterSpacing: '-0.4px',
                        color: colors.carbon_text_main || '#0F1729',
                        mb: '4px'
                    }}>
                        {tLocal.noEarningsYet}
                    </Typography>
                    <Typography sx={{
                        fontFamily: fonts.publicSans || fonts.poppins,
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        color: colors.carbon_text_secondary || '#65758B'
                    }}>
                        {tLocal.salesWillAppearHere}
                    </Typography>
                </Box>

            </Box>
        </Box>
    );
};

export default CarbonCreditsMyWallet;
